import { FC, useCallback, useEffect, useEffectEvent } from 'react';
import { StanButton, StanCard } from '@/components';
import { useBroadcastForm } from '../hooks/useBroadcastForm';
import { useBroadcastModals } from '../hooks/useBroadcastModals';
import { useBroadcastFormActions } from '../hooks/useBroadcastFormActions';
import { useBroadcastFormHandlers } from '../hooks/useBroadcastFormHandlers';
import { BroadcastAudienceSection } from './BroadcastAudienceSection';
import { BroadcastEmailContent } from './BroadcastEmailContent';
import { BroadcastFormActions } from './BroadcastFormActions';
import { PreviewBroadcastModal } from './PreviewBroadcastModal';
import { ScheduleBroadcastModal, ScheduleData } from './ScheduleBroadcastModal';
import { UnscheduleBroadcastModal } from './UnscheduleBroadcastModal';
import { TestSendModal } from '@/features/email-flows/emails/components';
import { useUser } from '@/queries';
import { useBroadcastEditorStore } from '../stores';
import { parseTimeFromAPI } from '@/lib/helpers';
import { isBroadcastEditable, isBroadcastScheduled } from '../logic/broadcast.logic';
import { useRecipientCount } from '../../queries';
import { useBroadcastActions } from '../hooks/useBroadcastActions';
import AlertCircle from '@/assets/icons/ic-alert-circle.svg';
import { createIconElement } from '@/lib';

interface BroadcastEditorProps {
    flowId: number;
    emailId?: number;
}

export const BroadcastEditor: FC<BroadcastEditorProps> = ({ flowId, emailId }) => {
    const { user } = useUser();
    const { handleViewRecipients } = useBroadcastActions();

    const defaultValues = useBroadcastEditorStore(state => state.defaultValues);
    const selectedTags = useBroadcastEditorStore(state => state.selectedTags);
    const selectedExcludedTags = useBroadcastEditorStore(state => state.selectedExcludedTags);
    const flowData = useBroadcastEditorStore(state => state.flowData);
    const setRecipientCount = useBroadcastEditorStore(state => state.setRecipientCount);
    const recipientCount = useBroadcastEditorStore(state => state.recipientCount);
    const generatedDraft = useBroadcastEditorStore(state => state.generatedDraft);

    // Fetch recipient count
    const { recipientCount: fetchedRecipientCount, isFetching: isFetchingRecipientCount } = useRecipientCount({
        includeTags: selectedTags,
        excludeTags: selectedExcludedTags,
        flowId,
        flowEmailId: emailId,
    });

    // Event handler that always uses latest store setter
    const updateRecipientCount = useEffectEvent((count: number) => {
        setRecipientCount(count);
    });

    // Update store when recipient count changes
    useEffect(() => {
        updateRecipientCount(fetchedRecipientCount);
    }, [fetchedRecipientCount]);

    // Compute status from flow data
    const isEditable = flowData ? isBroadcastEditable(flowData) : true;
    const isScheduled = flowData ? isBroadcastScheduled(flowData) : false;

    const formMethods = useBroadcastForm({
        defaultValues: defaultValues || {
            subject: '',
            body: '',
            tags: [],
            excludedTags: [],
        },
    });

    const {
        control,
        formState: { errors, isDirty },
        watch,
        setValue,
    } = formMethods;

    const currentSubject = watch('subject');
    const currentBody = watch('body');

    const modals = useBroadcastModals();

    // Form actions (mutations)
    const {
        handleSaveDraft: saveDraft,
        handleSendNow: sendNow,
        handleSchedule: schedule,
        isPending,
    } = useBroadcastFormActions({
        flowId,
        emailId: emailId || -1,
    });

    // Form handlers with validation
    const { handleSaveDraft, handleSend } = useBroadcastFormHandlers({
        formMethods,
        onSaveDraft: saveDraft,
        onSend: async () => {
            if (isScheduled) {
                modals.schedule.open();
            } else {
                modals.preview.open();
            }
        },
        recipientCount,
    });

    const updatesDisabled = isPending || !isEditable;

    // Allow saving as draft if:
    // 1. Form is dirty (user made changes), OR
    // 2. Broadcast is scheduled (to allow unscheduling), OR
    // 3. There's a generated draft that hasn't been saved yet
    const canSaveDraft = (isDirty || isScheduled || !!generatedDraft) && !updatesDisabled;

    const savedSchedule =
        isScheduled && flowData?.data?.specific_time && flowData.data.specific_time.date
            ? {
                  selectedDate: new Date(flowData.data.specific_time.date + 'T00:00:00'),
                  selectedTime: parseTimeFromAPI(flowData.data.specific_time.time || '12:00 AM'),
                  selectedTimezone:
                      flowData.data.specific_time.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
              }
            : undefined;

    const handleSendNowWithModal = useCallback(async () => {
        const formData = formMethods.getValues();
        await sendNow(formData);
        modals.preview.close();
    }, [formMethods, sendNow, modals.preview]);

    const handleScheduleWithModal = useCallback(
        async (scheduleData: ScheduleData) => {
            const formData = formMethods.getValues();
            await schedule(formData, scheduleData);
            modals.schedule.close();
        },
        [formMethods, schedule, modals.schedule]
    );

    // Show unschedule confirmation modal if broadcast is scheduled
    const handleSaveDraftClick = useCallback(() => {
        if (isScheduled && !isDirty) {
            modals.unschedule.open();
        } else {
            handleSaveDraft();
        }
    }, [isScheduled, isDirty, modals.unschedule, handleSaveDraft]);

    const handleConfirmUnschedule = useCallback(async () => {
        await handleSaveDraft();
        modals.unschedule.close();
    }, [handleSaveDraft, modals.unschedule]);

    return (
        <>
            <StanCard className='p-6'>
                {!isEditable && flowData && (
                    <div className='border-warning-intense bg-warning-dimmed mb-6 flex w-full justify-between gap-1 rounded-md border px-4 py-2 max-md:flex-col md:items-center md:gap-2'>
                        <div className='flex gap-1.5 md:items-center'>
                            {createIconElement(AlertCircle, { className: 'size-4.5 shrink-0 text-warning-bold' })}
                            <p className='text-body-xs md:text-body-sm font-medium'>
                                This broadcast has already been sent and cannot be edited.
                            </p>
                        </div>
                        <StanButton
                            name='broadcast-view-recipients'
                            variant='link'
                            size='xs'
                            onClick={() => handleViewRecipients(flowData)}
                            className='w-fit shrink-0 max-md:self-end'
                        >
                            View Recipients
                        </StanButton>
                    </div>
                )}

                <form className='w-full space-y-6'>
                    <BroadcastAudienceSection
                        setValue={setValue}
                        disabled={updatesDisabled}
                        isFetchingCount={isFetchingRecipientCount}
                    />

                    <BroadcastEmailContent
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        disabled={updatesDisabled}
                    />

                    <BroadcastFormActions
                        canSaveDraft={canSaveDraft}
                        isSent={!isEditable}
                        isScheduled={isScheduled}
                        disabled={updatesDisabled}
                        onTestSend={modals.testSend.open}
                        onSaveDraft={handleSaveDraftClick}
                        onSend={handleSend}
                    />
                </form>
            </StanCard>

            <TestSendModal
                isOpen={modals.testSend.isOpen}
                defaultEmail={user?.email || ''}
                onClose={modals.testSend.close}
                flowId={flowId}
                emailId={emailId}
                subject={currentSubject}
                body={currentBody}
            />

            <PreviewBroadcastModal
                isOpen={modals.preview.isOpen}
                flowId={flowId}
                flowEmailId={emailId}
                tags={[...selectedTags, ...selectedExcludedTags.map(tag => ({ ...tag, excluded: 1 }))]}
                recipientCount={recipientCount}
                onClose={modals.preview.close}
                onSendNow={handleSendNowWithModal}
                onSchedule={modals.schedule.open}
                disabled={recipientCount === 0}
                isLoading={isPending}
            />

            <ScheduleBroadcastModal
                isOpen={modals.schedule.isOpen}
                savedSchedule={savedSchedule}
                recipientCount={recipientCount}
                onClose={modals.schedule.close}
                onSchedule={handleScheduleWithModal}
                onSendNow={modals.showPreviewFromSchedule}
                disabled={recipientCount === 0}
                isLoading={isPending}
            />

            <UnscheduleBroadcastModal
                isOpen={modals.unschedule.isOpen}
                onConfirm={handleConfirmUnschedule}
                onCancel={modals.unschedule.close}
                isLoading={isPending}
            />
        </>
    );
};
